<template>
  <div class="p-6 dark:text-white" v-if="profile">
    <!-- Pillar Generation Notice -->
    <div
      v-if="showPillarNotice"
      class="bg-amber-100 border border-amber-400 text-amber-800 px-4 py-3 rounded relative mb-4 flex items-center justify-between"
    >
      <div>
        <strong class="font-bold">Reminder:</strong>
        <span class="ml-1">
          Profile changes require clicking "Generate Pillar" on the profiles list to take effect.
        </span>
      </div>
      <button @click="showPillarNotice = false" class="text-amber-800 hover:text-amber-900">
        <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
        </svg>
      </button>
    </div>

    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-semibold flex items-center dark:text-white">
        <button
          @click="goBackToList"
          class="mr-4 bg-blue-600 text-white px-4 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 text-sm font-medium flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clip-rule="evenodd"
            />
          </svg>
          Back
        </button>
        SNMP Profile: {{ profile.name }}
        <span v-if="!profile.org" class="ml-3 px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded-full">
          Global
        </span>
      </h1>

      <!-- Edit Profile Button -->
      <div class="flex space-x-2">
        <button
          v-if="canEdit || user?.is_staff"
          @click="$router.push({ name: 'EditSNMPProfile', params: { id: profile.id }, query: tenant.addSchemaParam() })"
          class="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-200"
        >
          Edit Profile
        </button>
        <button
          v-if="canDelete || user?.is_staff"
          @click="confirmDeleteProfile"
          class="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition duration-200"
        >
          Delete Profile
        </button>
      </div>
    </div>

    <!-- Profile Info Card -->
    <div class="bg-white/80 dark:bg-gray-800/70 backdrop-blur-md rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
      <h2 class="text-lg font-semibold mb-4">Profile Details</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label class="text-sm text-gray-500 dark:text-gray-400">Name</label>
          <p class="font-medium font-mono">{{ profile.name }}</p>
        </div>
        <div>
          <label class="text-sm text-gray-500 dark:text-gray-400">Organisation</label>
          <p class="font-medium">{{ profile.org_name || 'Global (all orgs)' }}</p>
        </div>
        <div>
          <label class="text-sm text-gray-500 dark:text-gray-400">Created</label>
          <p class="font-medium">{{ formatDateTime(profile.created_date) }}</p>
        </div>
        <div>
          <label class="text-sm text-gray-500 dark:text-gray-400">Last Modified</label>
          <p class="font-medium">{{ formatDateTime(profile.last_modified) }}</p>
        </div>
      </div>
      <div v-if="profile.description" class="mt-4">
        <label class="text-sm text-gray-500 dark:text-gray-400">Description</label>
        <p class="font-medium">{{ profile.description }}</p>
      </div>
    </div>

    <!-- Tabs (mirrors ProbeDetails tab styling) -->
    <div class="glass-card mb-2">
      <nav class="flex px-2 py-2">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          @click="activeTab = tab.key"
          :class="[
            'ml-2 group inline-flex items-center px-4 py-2 border-t border-l border-r rounded-t-md text-sm font-medium transition-colors duration-150 ease-in-out',
            activeTab === tab.key
              ? 'bg-white text-blue-600 border-gray-300 -mb-px dark:bg-gray-800 dark:text-blue-400'
              : 'bg-gray-100 text-gray-500 border-transparent hover:bg-gray-50 hover:text-blue-600 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-blue-400',
          ]"
        >
          {{ tab.label }}
          <span class="ml-2 px-2 py-0.5 text-xs rounded-full border border-gray-300/60 dark:border-gray-600/60">
            {{ getCount(tab.key) }}
          </span>
        </button>
      </nav>
    </div>

    <!-- Tab Content (split out of the tab-nav wrapper to match ProbeDetails) -->
    <div class="bg-white/80 dark:bg-gray-800/70 backdrop-blur-md rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">

      <!-- Tab Content -->
      <div class="p-6">
        <!-- Metrics Tab -->
        <div v-if="activeTab === 'metrics'">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold">Metrics (OIDs)</h3>
            <button
              v-if="canEdit || user?.is_staff"
              @click="$router.push({ name: 'AddSNMPMetric', params: { profileId: profile.id }, query: tenant.addSchemaParam() })"
              class="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm"
            >
              Add Metric
            </button>
          </div>

          <div v-if="profile.metrics && profile.metrics.length > 0" class="space-y-3">
            <div
              v-for="metric in profile.metrics"
              :key="metric.id"
              class="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
            >
              <div class="flex justify-between items-start">
                <div class="flex-1">
                  <div class="flex items-center mb-2">
                    <span class="font-semibold text-lg">{{ metric.name }}</span>
                    <span class="ml-2 px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-800">{{ metric.type }}</span>
                  </div>
                  <p class="text-sm text-gray-500 dark:text-gray-400 font-mono">OID: {{ metric.oid }}</p>
                  <p class="text-sm text-gray-600 dark:text-gray-300 mt-1">{{ metric.help_text }}</p>
                  <div v-if="metric.indexes" class="mt-2">
                    <span class="text-xs text-gray-500">Indexes:</span>
                    <span class="text-xs font-mono ml-1">{{ JSON.stringify(metric.indexes) }}</span>
                  </div>
                  <div v-if="metric.lookups" class="mt-1">
                    <span class="text-xs text-gray-500">Lookups:</span>
                    <span class="text-xs font-mono ml-1">{{ metric.lookups.length }} defined</span>
                  </div>
                </div>
                <div class="flex space-x-2">
                  <button
                    @click="$router.push({ name: 'EditSNMPMetric', params: { profileId: profile.id, metricId: metric.id }, query: tenant.addSchemaParam() })"
                    class="text-blue-600 hover:text-blue-800 p-1"
                    title="Edit"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                    </svg>
                  </button>
                  <button @click="deleteMetric(metric)" class="text-red-600 hover:text-red-800 p-1" title="Delete">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fill-rule="evenodd"
                        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <p v-else class="text-gray-500 text-center py-8">No metrics defined yet. Click "Add Metric" to create one.</p>
        </div>

        <!-- Walks Tab -->
        <div v-if="activeTab === 'walks'">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold">Walk OIDs</h3>
            <button
              v-if="canEdit || user?.is_staff"
              @click="$router.push({ name: 'AddSNMPWalk', params: { profileId: profile.id }, query: tenant.addSchemaParam() })"
              class="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm"
            >
              Add Walk OID
            </button>
          </div>

          <div v-if="profile.walks && profile.walks.length > 0">
            <table class="w-full">
              <thead>
                <tr class="border-b border-gray-200 dark:border-gray-700">
                  <th class="text-left py-2 px-4 font-medium">OID</th>
                  <th class="text-left py-2 px-4 font-medium">Description</th>
                  <th class="text-right py-2 px-4 font-medium w-24">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="walk in profile.walks"
                  :key="walk.id"
                  class="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <td class="py-3 px-4 font-mono text-sm">{{ walk.oid }}</td>
                  <td class="py-3 px-4 text-gray-600 dark:text-gray-300">{{ walk.description || '-' }}</td>
                  <td class="py-3 px-4 text-right">
                    <button
                      @click="$router.push({ name: 'EditSNMPWalk', params: { profileId: profile.id, walkId: walk.id }, query: tenant.addSchemaParam() })"
                      class="text-blue-600 hover:text-blue-800 p-1"
                      title="Edit"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                      </svg>
                    </button>
                    <button @click="deleteWalk(walk)" class="text-red-600 hover:text-red-800 p-1" title="Delete">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fill-rule="evenodd"
                          d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="text-gray-500 text-center py-8">No walk OIDs defined yet. Click "Add Walk OID" to create one.</p>
        </div>

        <!-- YAML Preview Tab -->
        <div v-if="activeTab === 'yaml'">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold">YAML Preview</h3>
            <button @click="copyYaml" class="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 text-sm">
              Copy YAML
            </button>
          </div>
          <pre class="border border-gray-300/40 dark:border-gray-600/40 p-4 rounded-lg overflow-x-auto text-sm font-mono">{{ yamlPreview }}</pre>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="deletingItem" class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white/80 dark:bg-gray-800/70 backdrop-blur-md p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
        <h3 class="text-lg font-semibold mb-4">Confirm Delete</h3>
        <p class="mb-4">Are you sure you want to delete this {{ deletingItem.type }}?</p>
        <p class="font-medium text-gray-600 dark:text-gray-300 mb-6 font-mono">
          {{ deletingItem.type === 'metric' ? deletingItem.item.name : deletingItem.item.oid }}
        </p>
        <div class="flex justify-end space-x-2">
          <button @click="deletingItem = null" class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400">
            Cancel
          </button>
          <button @click="confirmDelete" class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">Delete</button>
        </div>
      </div>
    </div>

    <!-- Toast -->
    <div
      v-if="toastMessage"
      :class="[
        'fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg transition-opacity duration-300 z-50',
        toastType === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white',
      ]"
    >
      {{ toastMessage }}
    </div>
  </div>

  <!-- Loading State -->
  <div v-else-if="!error" class="p-6">
    <p>Loading...</p>
  </div>

  <!-- Error State -->
  <PageError v-if="error" :error="error" :errorCode="errorCode" />
</template>

<script>
import {
  getSNMPProfileById,
  deleteSNMPProfile,
  deleteSNMPMetric,
  deleteSNMPWalk,
} from "@/services/snmpProfileService";
import PageError from "@/components/PageError.vue";
import { useStore } from "@/store";
import { formatError } from "@/utils/errorMessages";
import { useMultiTenant } from "@/composables/useMultiTenant";

export default {
  name: "SNMPProfileDetails",
  components: {
    PageError,
  },
  setup() {
    const tenant = useMultiTenant();
    return { tenant };
  },
  data() {
    return {
      profile: null,
      error: "",
      errorCode: "",
      activeTab: "metrics",
      deletingItem: null,
      canEdit: false,
      canDelete: false,
      showPillarNotice: false,
      toastMessage: "",
      toastType: "success",
      tabs: [
        { key: "metrics", label: "Metrics" },
        { key: "walks", label: "Walks" },
        { key: "yaml", label: "YAML Preview" },
      ],
    };
  },
  computed: {
    user() {
      return useStore.getUser();
    },
    currentSchema() {
      return this.$route.query.schemaName || this.tenant.currentSchemaName?.value || null;
    },
    yamlPreview() {
      if (!this.profile) return "";

      const profileData = {
        [this.profile.name]: {
          metrics: (this.profile.metrics || []).map((m) => {
            const entry = {
              name: m.name,
              oid: m.oid,
              type: m.type,
              help: m.help_text,
            };
            if (m.indexes) entry.indexes = m.indexes;
            if (m.lookups) entry.lookups = m.lookups;
            return entry;
          }),
          walk: (this.profile.walks || []).map((w) => w.oid),
        },
      };

      return this.toYaml(profileData);
    },
  },
  async mounted() {
    await this.tenant.fetchOrganizations();
    await this.tenant.loadInitialData(() => Promise.resolve());
    await this.fetchProfile();
    await this.checkPermissions();
    // Check for pillar notice flag from AddSNMPProfile redirect
    if (this.$route.query.showPillarNotice) {
      this.showPillarNotice = true;
    }
  },
  methods: {
    async checkPermissions() {
      if (this.user) {
        this.canEdit = await useStore.hasPermission("Can change SNMP Metric Profile");
        this.canDelete = await useStore.hasPermission("Can delete SNMP Metric Profile");
      }
    },
    goBackToList() {
      // Pass pillar notice flag if it was shown on this page
      const query = { ...this.tenant.addSchemaParam(), ...(this.showPillarNotice ? { showPillarNotice: true } : {}) };
      this.$router.push({ name: "SNMPProfiles", query });
    },
    async fetchProfile() {
      try {
        const profileId = this.$route.params.id;
        this.profile = await getSNMPProfileById(profileId, this.currentSchema);
      } catch (error) {
        console.error("Error fetching SNMP profile:", error);
        const { code, message } = formatError(error);
        this.errorCode = code;
        this.error = message;
      }
    },
    getCount(tabKey) {
      if (!this.profile) return 0;
      const countMap = {
        metrics: this.profile.metrics?.length || 0,
        walks: this.profile.walks?.length || 0,
        yaml: "-",
      };
      return countMap[tabKey];
    },
    formatDateTime(dateString) {
      if (!dateString) return "N/A";
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    },
    deleteMetric(metric) {
      this.deletingItem = { type: "metric", item: metric };
    },
    deleteWalk(walk) {
      this.deletingItem = { type: "walk", item: walk };
    },
    async confirmDelete() {
      if (!this.deletingItem) return;

      const { type, item } = this.deletingItem;

      try {
        if (type === "metric") {
          await deleteSNMPMetric(item.id, this.currentSchema);
        } else {
          await deleteSNMPWalk(item.id, this.currentSchema);
        }
        await this.fetchProfile();
        this.deletingItem = null;
        this.showToast(`${type === "metric" ? "Metric" : "Walk"} deleted successfully`, "success");
      } catch (error) {
        console.error("Error deleting:", error);
        this.showToast(`Failed to delete ${type}`, "error");
      }
    },
    async confirmDeleteProfile() {
      if (confirm(`Are you sure you want to delete profile "${this.profile.name}"? This will also delete all metrics and walks.`)) {
        try {
          await deleteSNMPProfile(this.profile.id, this.currentSchema);
          this.$router.push({ name: "SNMPProfiles", query: this.tenant.addSchemaParam() });
        } catch (error) {
          console.error("Error deleting profile:", error);
          this.showToast("Failed to delete profile", "error");
        }
      }
    },
    showToast(message, type = "success") {
      this.toastMessage = message;
      this.toastType = type;
      setTimeout(() => {
        this.toastMessage = "";
      }, 3000);
    },
    copyYaml() {
      navigator.clipboard.writeText(this.yamlPreview).then(() => {
        this.showToast("YAML copied to clipboard", "success");
      });
    },
    toYaml(obj, indent = 0) {
      const spaces = "  ".repeat(indent);
      let yaml = "";

      for (const [key, value] of Object.entries(obj)) {
        if (Array.isArray(value)) {
          yaml += `${spaces}${key}:\n`;
          for (const item of value) {
            if (typeof item === "object") {
              yaml += `${spaces}  - `;
              const itemYaml = this.toYaml(item, indent + 2).trim();
              const lines = itemYaml.split("\n");
              yaml += lines[0] + "\n";
              for (let i = 1; i < lines.length; i++) {
                yaml += `${spaces}    ${lines[i]}\n`;
              }
            } else {
              yaml += `${spaces}  - ${item}\n`;
            }
          }
        } else if (typeof value === "object" && value !== null) {
          yaml += `${spaces}${key}:\n`;
          yaml += this.toYaml(value, indent + 1);
        } else {
          yaml += `${spaces}${key}: ${value}\n`;
        }
      }

      return yaml;
    },
  },
};
</script>
